<script setup>
const props = defineProps({
  imageObj: {
    type: Object,
    required: true,
  },
  imageAlt: {
    type: String,
    required: true,
  },
  isLightBox: {
    type: Boolean,
    default: false,
  },
  priority: {
    type: Boolean,
    default: false,
  },
});

// COMPOSABLES
const { useImage } = useImages();

// CONSTANTS
const PICTURE_DEFAULT_OPTIONS = {
  vibrance: true,
  sharpen: true,
  improve: true,
};

const PICTURE_OPTIONS = {
  large: {
    height: 320,
    width: 1024,
  },
  medium: {
    height: 320,
    width: 768,
  },
  small: {
    height: 240,
    width: 416,
  },
};

// @devNote: .98 closes fractional-px gaps between adjacent min/max breakpoints (OS zoom / DPR).
const PRELOAD_MEDIA = {
  large: '(min-width: 1368px)',
  medium: '(min-width: 768px) and (max-width: 1367.98px)',
  small: '(max-width: 767.98px)',
};

function getPictureOptions(size) {
  const imageDimensions = props.isLightBox
    ? { width: PICTURE_OPTIONS[size].width }
    : { height: PICTURE_OPTIONS[size].height };
  return {
    ...PICTURE_DEFAULT_OPTIONS,
    ...imageDimensions,
  };
}

const largeImageUrl = computed(() =>
  useImage.getOptimizedImageUrl(props.imageObj?.large, getPictureOptions('large')),
);
const mediumImageUrl = computed(() =>
  useImage.getOptimizedImageUrl(props.imageObj?.medium, getPictureOptions('medium')),
);
const smallImageUrl = computed(() =>
  useImage.getOptimizedImageUrl(props.imageObj?.small, getPictureOptions('small')),
);

if (props.priority) {
  useHead(() => {
    const preloadSources = [
      {
        href: largeImageUrl.value,
        media: PRELOAD_MEDIA.large,
      },
      {
        href: mediumImageUrl.value,
        media: PRELOAD_MEDIA.medium,
      },
      {
        href: smallImageUrl.value,
        media: PRELOAD_MEDIA.small,
      },
    ];

    return {
      link: preloadSources
        .filter((source) => source.href)
        .map((source) => ({
          rel: 'preload',
          as: 'image',
          href: source.href,
          media: source.media,
        })),
    };
  });
}
</script>

<template>
  <picture>
    <source
      media="(min-width: 1368px)"
      :srcset="largeImageUrl"
      :width="PICTURE_OPTIONS.large.width"
      :height="PICTURE_OPTIONS.large.height"
    />
    <source
      media="(min-width: 768px)"
      :srcset="mediumImageUrl"
      :width="PICTURE_OPTIONS.medium.width"
      :height="PICTURE_OPTIONS.medium.height"
    />
    <source
      media="(max-width: 768px)"
      :srcset="smallImageUrl"
      :width="PICTURE_OPTIONS.small.width"
      :height="PICTURE_OPTIONS.small.height"
    />
    <img
      :src="smallImageUrl"
      :alt="imageAlt"
      :width="PICTURE_OPTIONS.small.width"
      :height="PICTURE_OPTIONS.small.height"
      :loading="priority ? 'eager' : 'lazy'"
      :fetchpriority="priority ? 'high' : undefined"
    />
  </picture>
</template>

<style scoped lang="scss">
img {
  display: block;
  max-width: unset;
  width: 100%;
  height: auto;
}
</style>
